import type { SystemAccessLogEntry, SystemAccessLogHistoryEntry } from '../types';
import { plainTextToHtml } from '../../../utils/htmlPlainTextConverter';

// Maps a stored error_reason category to a human-readable German display string.
// CONTRACT (R-1, RITA-1351): verbindliche Anzeigetexte per Kategorie.
export function formatErrorReason(category: string | null | undefined): string {
  switch (category) {
    case 'kein_empfaenger':
      return 'Keine E-Mail-Adresse hinterlegt';
    case 'adresse_ungueltig_abgelehnt':
      return 'E-Mail-Adresse ungültig oder abgelehnt';
    case 'nachricht_zu_gross':
      return 'Nachricht zu groß';
    case 'technisch':
      return 'Technischer Versandfehler';
    default:
      return 'Versandfehler';
  }
}

// Whether a stored error_reason category triggers the expandable technical detail (AK-3).
export function isTechnicalError(category: string | null | undefined): boolean {
  return category === 'technisch';
}

// Whether a log row may be manually cancelled via "Versand abbrechen".
//
// A genuinely user-scheduled action sits at status='scheduled',
// process_status='planned' AND always carries a concrete future `scheduled_at`.
//
// A claimed-but-unsent IMMEDIATE send that the cron's stale-claim recovery reset
// looks almost identical — status='scheduled', process_status='planned' — but it
// has `scheduled_at = NULL` (recovery deliberately keeps it NULL for immediate
// rows). Cancelling such a row would mark it 'cancelled' and the cron would never
// resend it, reintroducing exactly the silent drop the recovery exists to prevent
// (RITA-1307 Review P2). The `scheduled_at` check is the single discriminator
// between the two, so only a real scheduled action stays cancelable.
export function canCancelEntry(entry: SystemAccessLogEntry): boolean {
  return entry.status === 'scheduled' && entry.process_status === 'planned' && Boolean(entry.scheduled_at);
}

// Whether a log row may be edited via "✏️ Bearbeiten" (RITA-1317).
//
// Editing mirrors the backend gate in updateScheduledSystemAction: only a row that
// is still genuinely scheduled may be changed — status='scheduled',
// process_status='planned' AND a concrete future `scheduled_at`. The `scheduled_at`
// check is the same single discriminator canCancelEntry uses to exclude a
// claimed-but-unsent IMMEDIATE send (recovered with scheduled_at=NULL): such a row
// is in flight and the backend rejects edits on it (CANNOT_UPDATE_IN_FLIGHT), so it
// must not show the button either. A separate predicate (rather than reusing
// canCancelEntry) keeps the edit/cancel affordances independently documented and
// testable even though they share today's gate.
export function canEditEntry(entry: SystemAccessLogEntry): boolean {
  return entry.status === 'scheduled' && entry.process_status === 'planned' && Boolean(entry.scheduled_at);
}

// Whether a log row may be resent via "Erneut versenden" (RITA-1316).
//
// This MUST mirror the backend authorization exactly: resendSystemAction lets a row
// through on `isSent` (status='executed' AND a real send timestamp executed_at) plus
// `action_type='apply'` — nothing else (resendSystemAction.ts / sendState.ts). A
// claimed-but-unsent immediate send is excluded by the executed_at check alone: it
// sits at executed_at=NULL while the send is in flight (and the read side normalizes
// its process_status to 'planned' anyway), so resending it would race the pending
// send and double-mail. Scheduled/failed/cancelled rows were never sent and carry no
// executed_at.
//
// We deliberately do NOT also require `process_status === 'applied'`: that condition
// is both redundant (executed_at already excludes the in-flight claim) AND strictly
// narrower than the backend gate. A genuinely-sent legacy `apply` row can reach the
// UI with `process_status` still NULL — displayProcessStatus only normalizes NULL to
// 'planned' for `status='scheduled'` rows, an executed row passes its stored value
// (possibly NULL) straight through. Such a row is resendable server-side via GraphQL,
// so requiring 'applied' here would hide the button for a real, backend-authorized
// resend flow (Codex Review P1).
//
// Resending stays scoped to Zugangsbeantragungen (`action_type='apply'`). A successful
// cancel/change row also sits at status='executed' with an executed_at, but re-sending
// it would re-trigger the wrong provider-side business action — so the button stays
// hidden for those. The backend enforces the same restriction (Codex Review P1).
export function canResendEntry(entry: SystemAccessLogEntry): boolean {
  return entry.action_type === 'apply' && entry.status === 'executed' && Boolean(entry.executed_at);
}

export function formatDate(value: string | null): string {
  if (!value) return '—';
  const raw = /^\d+$/.test(value) ? Number(value) : value;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// `scheduled_at` is a date-only field anchored at 00:00 Europe/Berlin (the
// cron runs in that timezone). Three serialization shapes reach the UI and all
// must render the same calendar day regardless of the admin's browser TZ:
//   1. Date-only / naive-ISO strings ("2026-06-15", "2026-06-15T00:00:00",
//      "2026-06-15 00:00:00") WITHOUT a timezone designator — a naive datetime
//      would be parsed by `new Date()` in the *browser* timezone and could then
//      shift the calendar day east of Berlin. Pull the Y-M-D straight out of
//      the string so the rendered day never depends on the viewer's timezone.
//   2. Timezone-aware ISO instants ("2026-06-14T22:00:00.000Z", "...+02:00") —
//      an absolute point in time, NOT a literal calendar date. A trailing `Z`
//      or numeric offset must NOT be stripped: it has to be converted to Berlin
//      TZ, otherwise a Berlin-midnight instant serialized from a UTC runtime
//      (22:00Z the day before) would render one day early (RITA-1307 Review P2).
//   3. epoch-millis strings (Sequelize DATE -> GraphQL String) — render the
//      absolute instant explicitly in Berlin TZ.
export function formatScheduledDateBerlin(value: string | null): string {
  if (!value) return '—';
  // Date-only / naive-ISO ONLY (no `Z`, no ±HH:MM offset): read the calendar
  // date directly, no Date/TZ math. The trailing `(?![Z+])` / anchored `$`
  // ensures a timezone-aware instant falls through to the Berlin conversion
  // below instead of being misread as a literal day.
  const naiveMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ][\d:.]*)?$/);
  if (naiveMatch) {
    const [, year, month, day] = naiveMatch;
    return `${day}.${month}.${year}`;
  }
  const raw = /^\d+$/.test(value) ? Number(value) : value;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

// CONTRACT (R-1, RITA-1349): read the Europe/Berlin calendar day of an absolute instant as a
// canonical `YYYY-MM-DD` string WITHOUT routing through a locale-dependent toLocale* string.
//
// `toLocaleDateString('en-CA', …)` only renders ISO order (YYYY-MM-DD) under full-ICU; a
// small-/limited-ICU runtime silently falls back to en-US (MM/DD/YYYY) — the exact class that
// made the backend deterministically red in RITA-1347. Reading the parts of a fixed
// `Intl.DateTimeFormat('en-US', …)` BY TYPE (year/month/day) and reassembling them keeps the
// output `YYYY-MM-DD` even if the runtime resolves to en-US and reorders the parts (month-first).
// The `2-digit` month/day options preserve the zero-padding (06, not 6); `year: 'numeric'` is
// already four digits. The formatter is constructed per call so it always reflects the current
// runtime locale data (and stays spy-able in tests that force the en-US fallback).
function berlinCalendarDay(instant: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Berlin',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Convert a stored `scheduled_at` into the `YYYY-MM-DD` value an <input type="date">
// expects, anchored to the SAME Europe/Berlin calendar day formatScheduledDateBerlin
// renders. Used to prefill the edit dialog's date field so it shows the day the row is
// actually planned for, independent of the admin's browser timezone. Mirrors the three
// serialization shapes formatScheduledDateBerlin handles (naive date-only, TZ-aware ISO
// instant, epoch-millis string). Returns '' for an unparseable/empty value so the field
// stays empty rather than showing an invalid date.
export function toScheduledDateInputValue(value: string | null): string {
  if (!value) return '';
  const naiveMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ][\d:.]*)?$/);
  if (naiveMatch) {
    const [, year, month, day] = naiveMatch;
    return `${year}-${month}-${day}`;
  }
  const raw = /^\d+$/.test(value) ? Number(value) : value;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  // Read the Berlin calendar day locale-data-independently (see berlinCalendarDay): pins the
  // timezone to Berlin so the input day matches the rendered calendar day, and stays YYYY-MM-DD
  // even when a small-ICU runtime falls back from en-CA to en-US.
  return berlinCalendarDay(d);
}

// Earliest selectable scheduled day = "tomorrow" in Europe/Berlin — NOT the browser's
// local calendar day. `scheduled_at` is anchored at 00:00 Europe/Berlin and the backend
// rejects a date that is not strictly in the future (INVALID_SCHEDULED_DATE). Deriving
// tomorrow from the browser timezone (`new Date(); setDate(getDate() + 1)`) lets a viewer
// WEST of Berlin near midnight pick a day that is already today (or past) in Berlin — the
// backend then rejects it — and can block a viewer EAST of Berlin from a still-valid Berlin
// day. Anchor the picker's lower bound to the Berlin calendar day so it matches the backend
// regardless of viewer timezone (HOTSPOTS "Date-only-Feld … Tag-Shift" class; sibling of
// formatScheduledDateBerlin). `now` is injectable so the Berlin-day boundary is unit-testable
// across timezones.
export function getTomorrowDateBerlin(now: Date = new Date()): string {
  // Today's Europe/Berlin calendar day as YYYY-MM-DD, read locale-data-independently via
  // formatToParts (see berlinCalendarDay). The explicit timeZone makes this independent of the
  // runtime/browser timezone, and the part-by-type read keeps the value YYYY-MM-DD even when a
  // small-ICU runtime falls back from en-CA to en-US (MM/DD/YYYY).
  const berlinToday = berlinCalendarDay(now);
  // Add one CALENDAR day via UTC math so month/year rollover stays correct and no local
  // timezone re-enters: read the Berlin Y-M-D as a UTC midnight, +1 day, re-read UTC Y-M-D.
  const [year, month, day] = berlinToday.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day));
  next.setUTCDate(next.getUTCDate() + 1);
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(
    next.getUTCDate(),
  ).padStart(2, '0')}`;
}

// Resolve the mail_content to persist when SAVING an edit of a scheduled mail.
//
// The edit dialog shows the body as PLAIN TEXT (htmlToPlainText) so it is editable in a
// textarea, and converts back with plainTextToHtml on save. That round-trip is LOSSY: any
// HTML the plain-text view cannot represent (links, lists, inline formatting, tables) is
// gone. Applying it unconditionally would DESTROY the existing mail body even on a date-only
// edit where the operator never touched the text (Audit/Review P1).
//
// Guard: only convert when the operator actually CHANGED the text. If the current textarea
// value still equals the plain text we seeded the field with, the body is untouched — persist
// the ORIGINAL HTML verbatim, never the flattened round-trip. Only a real text change goes
// through plainTextToHtml (unavoidable for a plain-text editor, and now the operator's
// explicit intent).
export function resolveScheduledMailContent(
  originalHtml: string,
  seededPlainText: string,
  editedPlainText: string,
): string {
  if (editedPlainText === seededPlainText) {
    return originalHtml;
  }
  return plainTextToHtml(editedPlainText);
}

// Whether the scheduled-mail edit dialog carries a REAL change worth persisting.
//
// updateScheduledSystemAction ALWAYS appends an `updated` history entry and bumps the
// "geändert"/Versions-Badge — so firing it on a no-op (operator opens the dialog and saves
// without touching anything) inflates the revision-safe audit trail with a change that never
// happened (Review/Audit P1). The dialog is dirty only when the body OR the planned date
// actually differs from what the fields were seeded with on open. The body comparison is the
// SAME raw (untrimmed) equality resolveScheduledMailContent uses, so "dirty" and "persist the
// original HTML verbatim" agree on what "unchanged" means.
export function isScheduledEditDirty(
  seededPlainText: string,
  editedPlainText: string,
  seededDate: string,
  editedDate: string,
): boolean {
  return editedPlainText !== seededPlainText || editedDate !== seededDate;
}

// A `planned` row normally carries a future `scheduled_at`. But a claimed-but-
// unsent IMMEDIATE send is ALSO normalized to process_status='planned' with
// scheduled_at=null (displayProcessStatus maps the in-flight claim to 'planned';
// stale-claim recovery keeps scheduled_at NULL for immediate rows). Such a row
// was requested as "sofort", so it must NOT be rendered as a terminierter /
// geplanter Versand. Centralized here so the detail heading and the metadata
// grid stay in agreement and the immediate case is unit-testable.
export interface PlannedSendDescriptor {
  // Metadata-grid label + value (mirrors the "Sofort"/"Terminiert" wording the
  // applied/not_sent cases already use).
  gridKey: string;
  gridValue: string;
  // "Aktuelle E-Mail" heading subtitle.
  headingSubtitle: string;
}

export function describePlannedSend(scheduledAt: string | null): PlannedSendDescriptor {
  if (scheduledAt) {
    const date = formatScheduledDateBerlin(scheduledAt);
    return {
      gridKey: 'Geplanter Versand',
      gridValue: date,
      headingSubtitle: `wird am ${date} versendet`,
    };
  }
  return {
    gridKey: 'Zeitpunkt',
    gridValue: 'Sofort',
    headingSubtitle: 'wird sofort versendet',
  };
}

export function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const raw = /^\d+$/.test(value) ? Number(value) : value;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// History rows can share the same second-level `createdAt` when
// updateScheduledSystemAction appends `updated` + `sent` back-to-back. Sort by
// (createdAt, id) so the UI always picks the truly newest row as "aktuell" and
// pairs the right predecessor. Defensive: the backend already orders by
// (createdAt ASC, id ASC), but we re-sort so the panel stays correct even if
// the server contract changes.
export function sortHistory(history: SystemAccessLogHistoryEntry[]): SystemAccessLogHistoryEntry[] {
  return [...history].sort((a, b) => {
    const aTime = new Date(/^\d+$/.test(a.createdAt) ? Number(a.createdAt) : a.createdAt).getTime();
    const bTime = new Date(/^\d+$/.test(b.createdAt) ? Number(b.createdAt) : b.createdAt).getTime();
    if (aTime !== bTime) return aTime - bTime;
    return a.id - b.id;
  });
}

// History entry types that represent an actual send ATTEMPT and therefore carry
// the subject that really went out (or was really attempted) in their snapshot.
// `created`/`updated` are pre-send planning snapshots: their subject is what the
// row looked like BEFORE the send, which can diverge from what actually went out
// once the advisor data changed between planning and send. They must NOT be used
// as the "sent subject" — see resolveDisplaySubject (RITA-1307 Review P1).
const SENT_SUBJECT_ENTRY_TYPES: ReadonlyArray<SystemAccessLogHistoryEntry['entry_type']> = ['sent', 'resent', 'failed'];

// Planning history entries (`created`/`updated`) carry the last subject the action
// was scheduled with. They are the correct subject ONLY for a terminal row that
// never produced a send-attempt subject and has no durable row snapshot — most
// importantly a CANCELLED action, whose `cancelled` history entry is written with
// `mail_subject_snapshot = null` and whose row snapshot / `current_subject` are
// null too. Used strictly as the LAST fallback in resolveDisplaySubject so it can
// never shadow the real sent subject of an actually-sent row (RITA-1307 Review P2).
const PLANNED_SUBJECT_ENTRY_TYPES: ReadonlyArray<SystemAccessLogHistoryEntry['entry_type']> = ['created', 'updated'];

// Subject shown in the mail detail header. The right source depends on whether a
// freshly-confirmed resend is still waiting for its authoritative log refetch
// (`resendPending`, owned by the panel's resend latch):
//
//  - resendPending = true — TRANSIENT window only: the resend mutation already
//    merged the fresh resend-time subject into the row's `current_subject`, but the
//    panel's GetAdvisorSystemLog refetch has not landed (or died), so the cached
//    `history` still shows the PRE-resend send subject. Prefer `current_subject` so
//    the header shows what just went out, never the stale cached history (RITA-1316
//    Review C/P1).
//
//  - resendPending = false — STEADY STATE (refetch landed, and every fresh page
//    load): the newest TERMINAL send-attempt history entry (`sent`/`resent`/`failed`)
//    is the AUTHORITATIVE, transactionally-committed record of the subject that went
//    out. The row snapshot behind `current_subject` is only a BEST-EFFORT post-send
//    write (`persistResendSnapshot`) that the backend deliberately swallows on a
//    transient DB error — so it can lag behind a committed `resent` history entry.
//    Ranking `current_subject` above the history would then render the stale
//    pre-resend subject behind a fresh `resent` entry after a successful refetch
//    (Codex Review P1). Priority in this state:
//      1. A still-`planned` row has no send-attempt entry → its live `current_subject`
//         (recomputed from current advisor data — what the cron will send).
//      2. The newest terminal `sent`/`resent`/`failed` snapshot — the subject that
//         actually went out. `created`/`updated` are pre-send planning snapshots and
//         are deliberately ignored here (RITA-1307 Review P1).
//      3. Fallback to the durable row snapshot (`current_subject`) for an already-sent
//         row whose best-effort terminal history append was swallowed and left the
//         history without a subject (RITA-1307 Review P1).
//      4. Last resort: the newest `created`/`updated` planning subject for a terminal
//         UNSENT row (e.g. a cancelled scheduled action), so the header shows the real
//         planned subject instead of "—" (RITA-1307 Review P2).
export function resolveDisplaySubject(entry: SystemAccessLogEntry, resendPending = false): string | null {
  const sorted = sortHistory(entry.history);
  const latestSentSubject =
    [...sorted].reverse().find((h) => SENT_SUBJECT_ENTRY_TYPES.includes(h.entry_type) && h.mail_subject_snapshot)
      ?.mail_subject_snapshot || null;
  const latestPlannedSubject =
    [...sorted].reverse().find((h) => PLANNED_SUBJECT_ENTRY_TYPES.includes(h.entry_type) && h.mail_subject_snapshot)
      ?.mail_subject_snapshot || null;
  if (resendPending) {
    return entry.current_subject || latestSentSubject || latestPlannedSubject;
  }
  return (
    (entry.process_status === 'planned' && entry.current_subject) ||
    latestSentSubject ||
    entry.current_subject ||
    latestPlannedSubject
  );
}

// On a failed send the backend intentionally leaves `executed_at` NULL: the row
// is claimed as `executed` before the SMTP call and only rolled back to
// `failed` afterwards, without stamping a timestamp. The real attempt time lives
// in the latest `failed` history entry. Use this everywhere a `not_sent` row
// needs its attempt date, so the audit log shows the failure day — not the
// creation day — even when a scheduled mail fails later in cron.
export function getLastFailedAttempt(history: SystemAccessLogHistoryEntry[]): SystemAccessLogHistoryEntry | null {
  const fails = sortHistory(history).filter((entry) => entry.entry_type === 'failed');
  return fails.length ? fails[fails.length - 1] : null;
}

// `mail_subject_snapshot` is null when a history entry left the subject
// unchanged (updateScheduledSystemAction / cancelScheduledSystemAction write
// null on purpose). For the diff we carry the last known subject forward so an
// unchanged subject is shown as-is instead of looking deleted. `idx` is the
// position of `entry` inside `sorted`.
export function resolveSubject(
  entry: SystemAccessLogHistoryEntry | null,
  sorted: SystemAccessLogHistoryEntry[],
  idx: number,
): string | null {
  if (!entry) return null;
  if (entry.mail_subject_snapshot) return entry.mail_subject_snapshot;
  for (let i = Math.min(idx, sorted.length - 1); i >= 0; i -= 1) {
    if (sorted[i].mail_subject_snapshot) return sorted[i].mail_subject_snapshot;
  }
  return null;
}

// Only `created`/`updated` rows carry the authoritative schedule decision: a
// concrete date, or an explicit null. A null on an `updated` row is a real
// "geplant → sofort"-Wechsel (updateScheduledSystemAction writes scheduled_at:
// null on the `updated` entry when the send is switched to immediate) and MUST
// be shown as removed — never carried forward, otherwise the diff shows the old
// date on both sides and the removal becomes invisible.
//
// Terminal rows (`sent`/`resent`/`failed`/`cancelled`) don't restate the
// schedule; they inherit the last schedule-setting decision. That inherited
// value may itself be null (the send was switched to immediate) — in which case
// nothing is shown, which is correct.
const SCHEDULE_SETTING_TYPES: ReadonlyArray<SystemAccessLogHistoryEntry['entry_type']> = ['created', 'updated'];

export function resolveScheduledAt(
  entry: SystemAccessLogHistoryEntry | null,
  sorted: SystemAccessLogHistoryEntry[],
  idx: number,
): string | null {
  if (!entry) return null;
  if (SCHEDULE_SETTING_TYPES.includes(entry.entry_type)) {
    return entry.scheduled_at;
  }
  for (let i = Math.min(idx, sorted.length - 1); i >= 0; i -= 1) {
    if (SCHEDULE_SETTING_TYPES.includes(sorted[i].entry_type)) {
      return sorted[i].scheduled_at;
    }
  }
  return null;
}

// Version label per history entry, mirroring the mockup:
// created → "vN · Original", updated → "vN", sent/resent → "N. Versand",
// failed → "Versuch N", cancelled → "abgebrochen".
export function computeHistoryBadges(sorted: SystemAccessLogHistoryEntry[]): string[] {
  let version = 0;
  let sendCount = 0;
  let failCount = 0;
  return sorted.map((h) => {
    switch (h.entry_type) {
      case 'created':
        version += 1;
        return `v${version} · Original`;
      case 'updated':
        version += 1;
        return `v${version}`;
      case 'sent':
      case 'resent':
        sendCount += 1;
        return `${sendCount}. Versand`;
      case 'failed':
        failCount += 1;
        return `Versuch ${failCount}`;
      case 'cancelled':
        return 'abgebrochen';
      default:
        return h.entry_type;
    }
  });
}
